/**
 * Analytics — organizer dashboard at `/dashboard/analytics`.
 *
 * Layout (desktop):
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │  Header + range picker                                        │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │  KPI cards × 4 (responsive grid)                              │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │  Revenue trend (line)        │  Invitation status (donut)     │
 *   ├──────────────────────────────┼────────────────────────────────┤
 *   │  Invitations created (bar)   │  Customer growth (line)        │
 *   ├──────────────────────────────┼────────────────────────────────┤
 *   │  Top events table            │  Recent activities feed        │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Mobile: every block stacks at the same vertical order.
 *
 * Data: a single `/v1/analytics/dashboard?range=<r>` call powers every block.
 * Re-fetched on range change. The backend caches per (user, range) for 5 min
 * so quickly toggling tabs is effectively free.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { DashboardHeader } from '@/src/modules/components/Header'
import { Button } from '@/src/modules/components/Button'
import { Icons } from '@/src/modules/components/Icons'
import { useAuth } from '@/src/modules/auth/AuthProvider'
import { useFormat } from '@/src/infrastructure/lib/format'
import { fetchDashboard } from '@/src/modules/analytics/api'
import type { AnalyticsRange, DashboardPayload } from '@/src/modules/analytics/types'
import { KpiCard } from '@/src/modules/analytics/components/KpiCard'
import { RangePicker } from '@/src/modules/analytics/components/RangePicker'
import { ChartCard } from '@/src/modules/analytics/components/ChartCard'
import { LineChart } from '@/src/modules/analytics/charts/LineChart'
import { BarChart } from '@/src/modules/analytics/charts/BarChart'
import { DonutChart } from '@/src/modules/analytics/charts/DonutChart'
import { TopEventsTable } from '@/src/modules/analytics/components/TopEventsTable'
import { RecentActivityList } from '@/src/modules/analytics/components/RecentActivityList'

export function Analytics() {
  const { user } = useAuth()
  const fmt = useFormat()

  const [range, setRange] = useState<AnalyticsRange>('30d')
  const [data, setData] = useState<DashboardPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (r: AnalyticsRange) => {
    setLoading(true)
    setError(null)
    try {
      const payload = await fetchDashboard(r)
      setData(payload)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load(range)
  }, [load, range])

  return (
    <div className="flex-1 flex flex-col min-h-screen bg-surface-lowest pb-20 md:pb-8">
      <DashboardHeader
        title="Analytics"
        description={`Welcome back${user?.displayName ? ', ' + user.displayName : ''}. Your business at a glance.`}
      />

      <div className="px-4 md:px-8 py-6 md:py-8 max-w-7xl mx-auto w-full flex flex-col gap-6">
        {/* Filter bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm text-on-surface-variant">
            {data
              ? `Last refreshed ${fmt.dateTime(data.generatedAt)}`
              : 'Loading…'}
          </div>
          <div className="flex items-center gap-3">
            <RangePicker value={range} onChange={setRange} />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => load(range)}
              disabled={loading}
              className="flex items-center gap-1.5"
              title="Refresh now"
            >
              <Icons.RotateCcw size={14} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-rose-500/10 border border-rose-500/30 text-rose-600 rounded-xl px-4 py-3 text-sm">
            {error}{' '}
            <button onClick={() => load(range)} className="underline font-medium">
              Try again
            </button>
          </div>
        )}

        {loading && !data && <SkeletonGrid />}

        {data && (
          <>
            {/* KPI row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard
                label="Total Invitations"
                value={fmt.number(data.summary.totalInvitations)}
                icon={<Icons.Calendar size={18} />}
                changePct={data.summary.invitationsTrendPct}
                caption={`${data.summary.invitationsSent} live`}
              />
              <KpiCard
                label="Active Events"
                value={fmt.number(data.summary.activeEvents)}
                icon={<Icons.Rocket size={18} />}
                hideTrend
                caption={`${data.summary.pendingInvitations} pending`}
              />
              <KpiCard
                label="RSVP Success Rate"
                value={`${data.summary.successRate}%`}
                icon={<Icons.CheckCircle size={18} />}
                hideTrend
                caption={`${fmt.number(data.summary.totalRsvps)} total responses`}
              />
              <KpiCard
                label="Total Views"
                value={fmt.number(data.summary.totalViews)}
                icon={<Icons.Eye size={18} />}
                hideTrend
                caption={`${fmt.number(data.summary.totalShares)} total shares`}
              />
            </div>

            {/* Secondary KPI row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard
                label="Total Customers"
                value={fmt.number(data.summary.totalCustomers)}
                icon={<Icons.Users size={18} />}
                hideTrend
                caption="Unique guests (30d)"
              />
              <KpiCard
                label="Total Shares"
                value={fmt.number(data.summary.totalShares)}
                icon={<Icons.Share size={18} />}
                hideTrend
              />
              <KpiCard
                label="Pending Invites"
                value={fmt.number(data.summary.pendingInvitations)}
                icon={<Icons.Clock size={18} />}
                hideTrend
                caption="Awaiting publish"
              />
              <KpiCard
                label="Failed Sends"
                value={fmt.number(data.summary.failedInvitations)}
                icon={<Icons.Warning size={18} />}
                hideTrend
                caption="Need attention"
              />
            </div>

            {/* Charts row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <ChartCard
                title="Invitations created"
                description="Per-day creation count across this range."
                className="lg:col-span-2"
              >
                <BarChart
                  data={data.charts.invitationTrend}
                  ariaLabel="Invitations created over time"
                />
              </ChartCard>
              <ChartCard
                title="RSVPs received"
                description="Accepted, declined, and tentative replies."
              >
                <DonutChart
                  data={data.charts.rsvpStatus}
                  centerLabel={fmt.number(data.summary.totalRsvps)}
                  centerSubLabel="responses"
                  ariaLabel="RSVP status distribution"
                />
              </ChartCard>
            </div>

            {/* Charts row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <ChartCard
                title="Customer growth"
                description="Guest responses bucketed across the range."
                className="lg:col-span-2"
              >
                <LineChart
                  data={data.charts.customerGrowth}
                  accentClassName="text-emerald-500"
                  ariaLabel="Guest responses over time"
                />
              </ChartCard>

              <ChartCard
                title="Invitation status"
                description="Breakdown of every invitation you own."
              >
                <DonutChart
                  data={data.charts.invitationStatus}
                  centerLabel={fmt.number(data.summary.totalInvitations)}
                  centerSubLabel="invitations"
                  ariaLabel="Invitation status distribution"
                />
              </ChartCard>
            </div>

            {/* Top events */}
            <ChartCard
              title="Top events"
              description="Your best performers by engagement."
            >
              <TopEventsTable rows={data.topEvents} />
            </ChartCard>

            {/* Recent activity */}
            <ChartCard
              title="Recent activity"
              description="Latest invitations, RSVPs, and payments."
            >
              <RecentActivityList rows={data.recentActivities} />
            </ChartCard>
          </>
        )}
      </div>
    </div>
  )
}

function SkeletonGrid() {
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-28 rounded-2xl bg-surface border border-outline-variant/20 animate-pulse"
          />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="h-64 lg:col-span-2 rounded-2xl bg-surface border border-outline-variant/20 animate-pulse" />
        <div className="h-64 rounded-2xl bg-surface border border-outline-variant/20 animate-pulse" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="h-64 lg:col-span-2 rounded-2xl bg-surface border border-outline-variant/20 animate-pulse" />
        <div className="h-64 rounded-2xl bg-surface border border-outline-variant/20 animate-pulse" />
      </div>
    </div>
  )
}
